import enums from '@/utils/enums';
import { cloneDeep } from 'lodash';

const state = {
  list: [],
  design_request: {},
  edit_design_request: {},
  designer: {},
  past_designers: []
};

const getters = {
  list(state) {
    return state.list || [];
  },
  design_request(state) {
    return state.design_request || {};
  },
  edit_design_request(state) {
    return state.edit_design_request || {};
  },
  designer(state) {
    return state.designer || {};
  },
  past_designers(state) { return state.past_designers || []; },
  completed(state, getters) {
    return getters.design_request.status?.id === enums.DESIGN_REQUEST_STATUS.COMPLETED;
  },
  cancelled(state, getters) {
    return getters.design_request.status?.id === enums.DESIGN_REQUEST_STATUS.CANCELLED;
  },
  is_designer(state, getters, rootState, rootGetters) {
    return getters.design_request.designer?.id === rootGetters['user/userInfoId'];
  },
  is_designer_or_admin(state, getters, rootState, rootGetters) {
    return getters.is_designer || rootGetters['user/isAdmin'];
  },
  allow_save(state, getters) {
    return !getters.completed && !getters.cancelled;
  }
};

const mutations = {
  SET_LIST(state, list) {
    state.list = list;
  },
  SET_DESIGN_REQUEST(state, item) {
    state.design_request = cloneDeep(item);
    state.edit_design_request = cloneDeep(item);
    const index = state.list.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      this._vm.$set(state.list, index, cloneDeep(item));
    } else {
      state.list.push(cloneDeep(item));
    }
  },
  SET_EDIT_DESIGN_REQUEST(state, item) {
    state.edit_design_request = cloneDeep(item);
  },
  DELETE_DESIGN_REQUEST(state, id) {
    const index = state.list.findIndex((x) => x.id === id);
    if (index >= 0) {
      state.list.splice(index, 1);
    }
  },
  SET_DESIGNER(state, item) {
    state.designer = item;
  },
  SET_PAST_DESIGNERS(state, list) {
    state.past_designers = list;
  },
  SET_REVIEW_ID(state, id) {
    this._vm.$set(state.design_request, 'reviewId', id);
  },
  SET_ORDER_ID(state, id) {
    this._vm.$set(state.design_request, 'orderId', id);
  }
};

const actions = {
  async getList({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get('designRequest/getList')
        .then((response) => {
          commit('SET_LIST', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async get({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get(`designRequest/get?id=${id}`)
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getDesigner({ commit }, id) {
    return new Promise((resolve, reject) => {
      if (!id) {
        commit('SET_DESIGNER', {});
        resolve();
        return;
      }
      this._vm.$api
        .get(`designRequest/getDesigner?id=${id}`)
        .then((response) => {
          commit('SET_DESIGNER', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async setDesigner({ commit }, item) {
    return new Promise((resolve, reject) => {
      commit('SET_DESIGNER', item);
      resolve();
    });
  },
  async getPastDesigners({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get('designRequest/getPastDesigners')
        .then((response) => {
          commit('SET_PAST_DESIGNERS', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getAssigned({ commit }) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .get('designRequest/getAssigned')
        .then((response) => {
          commit('SET_LIST', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  setEditDesignRequest({ commit }, item) {
    return new Promise((resolve, reject) => {
      commit(
        'SET_EDIT_DESIGN_REQUEST',
        Object.assign({ coreProduct: { id: 0 } }, item)
      );
      resolve();
    });
  },
  async add({ commit }, req) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      for (const file of req.designFiles) {
        formData.append('design-file', file);
      }
      for (const file of req.imageFiles) {
        formData.append('image-file', file);
      }
      formData.append('payload', JSON.stringify(req.payload));
      this._vm.$api
        .post('designRequest/add', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async apply({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post(`designRequest/apply?designRequestId=${id}`)
        .then((response) => {
          if (response.data) {
            commit('SET_DESIGN_REQUEST', response.data);
          }
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async claim({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post(`designRequest/claim?designRequestId=${id}`)
        .then((response) => {
          if (response.data) {
            commit('SET_DESIGN_REQUEST', response.data);
          }
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async placeOrder({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post('designRequest/placeOrder', req)
        .then((response) => {
          var result = response.data;
          if (result.success) {
            commit('SET_ORDER_ID', response.data.orderId);
          }
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async complete({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post(`designRequest/complete?designRequestId=${req.id}`)
        .then((response) => {
          if (response.data) {
            commit('SET_DESIGN_REQUEST', response.data);
          }
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async update({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post('designRequest/update', req)
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async updatePrice({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post(`designRequest/updatePrice?id=${req.id}&basePrice=${req.basePrice}`)
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async addFiles({ commit }, req) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      for (var file of req.files) {
        formData.append(req.isDesign ? 'design-file' : 'image-file', file);
      }
      this._vm.$api
        .post(`designRequest/addFiles?designRequestId=${req.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async cancel({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .post(`designRequest/cancel?designRequestId=${req.id}&reason=${req.reason}`)
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async delete({ commit }, id) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .delete(`designRequest/delete?id=${id}`)
        .then((response) => {
          commit('DELETE_DESIGN_REQUEST', id);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async deleteFile({ commit }, req) {
    return new Promise((resolve, reject) => {
      this._vm.$api
        .delete(`designRequest/deleteFile?id=${req.id}&designRequestId=${req.designRequestId}`)
        .then((response) => {
          commit('SET_DESIGN_REQUEST', response.data);
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
